import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsList from "@/components/CardsList";

import {StaffAPI} from "@/api/staff";

import AddIcon from "@mui/icons-material/Add";

import {ERROR_OPERATION} from "@/utils/constants";
import {staffRoleName} from "@/utils/clubSnapshot";

import "./index.scss";

// Ordine dei ruoli nell'elenco (nomi italiani o inglesi del backend)
const ROLE_ORDER = [
	/responsabile|head youth/i,
	/coordinat/i,
	/^allenatore(?! dei portieri)|head coach/i,
	/vice|second/i,
	/portier|goalkeeper/i,
	/preparator|performance/i,
	/fisioterap|physio/i,
];

const roleOrder = (staff) => {
	const index = ROLE_ORDER.findIndex((r) => r.test(staffRoleName(staff)));
	return index === -1 ? ROLE_ORDER.length : index;
};

function ManagerStaffsPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [staffs, setStaffs] = useState([]);
	const [filtersData, setFiltersData] = useState(
		JSON.parse(sessionStorage.getItem("manager_staffs_filters")) || {},
	);

	const filters = [
		{
			key: "name",
			label: t("name"),
			value: filtersData?.name,
			setValue: (value) => {
				sessionStorage.setItem(
					"manager_staffs_filters",
					JSON.stringify({name: value}),
				);
				setFiltersData({...filtersData, name: value});
			},
		},
	];

	const getStaffs = async () => {
		setLoading(true);
		try {
			const response = await StaffAPI.list({
				...filtersData,
				prospect: false,
				seasonId,
			});
			setStaffs(
				[...(response || [])].sort(
					(a, b) =>
						roleOrder(a) - roleOrder(b) ||
						(a.lastName ?? "")
							.toString()
							.localeCompare((b.lastName ?? "").toString(), "it", {
								sensitivity: "base",
							}),
				),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			getStaffs();
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [filtersData]);

	const getListConfiguration = () => {
		return {
			data: staffs,
			onClick: (staff) => navigate("/manager/staffs/" + staff.id),
		};
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			title: "Aggiungi staff",
			onClick: () => navigate("/manager/staffs/new"),
		},
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Staff"
				actions={<MainActions actions={mainActions} />}
			/>
			<CardsList
				breadcrumbs
				filtersData={filters}
				type="staff"
				listConfiguration={getListConfiguration()}
			/>
		</Page>
	);
}

export default ManagerStaffsPage;
